import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api.ts'

interface Lookup {
  found: boolean
  unitId?: string
  model?: string
  manufacturerName?: string
  customerId?: string | null
  customerName?: string | null
  warrantyActive?: boolean
  suggestedType?: 'gwarancyjny' | 'pogwarancyjny'
}
interface ClientHit {
  id: string
  name: string
}

/**
 * A service ticket starts from a serial number (SPEC 5.5): type it, and the machine
 * tells us the customer and whether it's under warranty. If it isn't ours, pick the
 * customer by hand and describe the machine.
 */
export function NewTicketDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [serial, setSerial] = useState('')
  const [lookup, setLookup] = useState<Lookup | null>(null)
  const [foreign, setForeign] = useState(false)
  const [machineDesc, setMachineDesc] = useState('')
  const [clientQ, setClientQ] = useState('')
  const [clients, setClients] = useState<ClientHit[]>([])
  const [customerId, setCustomerId] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [type, setType] = useState<'gwarancyjny' | 'pogwarancyjny' | 'przeglad'>('pogwarancyjny')
  const [fault, setFault] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  // Serial lookup, debounced.
  useEffect(() => {
    if (foreign || serial.trim().length < 3) {
      setLookup(null)
      return
    }
    const t = setTimeout(() => {
      api
        .get<Lookup>(`/api/service/lookup?serial=${encodeURIComponent(serial.trim())}`)
        .then((r) => {
          setLookup(r)
          if (r.found) {
            if (r.customerId) {
              setCustomerId(r.customerId)
              setCustomerName(r.customerName ?? '')
            }
            if (r.suggestedType) setType(r.suggestedType)
          }
        })
        .catch(() => setLookup(null))
    }, 250)
    return () => clearTimeout(t)
  }, [serial, foreign])

  // Client search (foreign machine or unknown serial).
  useEffect(() => {
    if (clientQ.trim().length < 2) return
    const t = setTimeout(() => {
      api.get<{ clients: ClientHit[] }>(`/api/clients?search=${encodeURIComponent(clientQ.trim())}`).then((r) => setClients(r.clients.slice(0, 6))).catch(() => {})
    }, 130)
    return () => clearTimeout(t)
  }, [clientQ])

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ ticket: { id: string } }>('/api/service-tickets', {
        customerId,
        unitId: !foreign && lookup?.found ? lookup.unitId : undefined,
        serialNumberText: serial.trim() || undefined,
        machineDescription: foreign ? machineDesc.trim() : undefined,
        serviceType: type,
        faultDescription: fault.trim() || undefined,
      })
      onCreated(r.ticket.id)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się utworzyć zgłoszenia')
      setBusy(false)
    }
  }

  // Unknown serial without the "obca maszyna" tick would reach the API as a
  // ticket with neither a unit nor a description — refused there; say it here.
  const canSubmit = customerId && (foreign ? machineDesc.trim() : lookup?.found)

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-20" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 pt-4 pb-2">
          <h2 className="font-bold text-slate-800">Nowe zgłoszenie serwisowe</h2>
          <p className="text-xs text-slate-400">Zacznij od numeru seryjnego — maszyna podpowie klienta i gwarancję.</p>
        </div>

        <div className="px-4 pb-4 space-y-3">
          {!foreign && (
            <>
              <input
                ref={ref}
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                placeholder="Numer seryjny…"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:border-brand-600"
              />
              {lookup && (
                <div className={`text-sm rounded-md px-3 py-2 ${lookup.found ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  {lookup.found ? (
                    <>
                      <div className="text-green-900">
                        {lookup.model} · {lookup.manufacturerName}
                      </div>
                      <div className="text-xs text-green-700">
                        {lookup.customerName ?? 'klient nieznany'} ·{' '}
                        {lookup.warrantyActive ? 'w gwarancji' : 'po gwarancji'}
                      </div>
                    </>
                  ) : (
                    <div className="text-amber-800 text-xs">
                      Nie znaleziono egzemplarza o tym numerze — zgłoszenie da się założyć tylko jako obcą maszynę.{' '}
                      <button onClick={() => setForeign(true)} className="underline">
                        To obca maszyna →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {foreign && (
            <div className="space-y-2">
              <input value={machineDesc} onChange={(e) => setMachineDesc(e.target.value)} placeholder="Opis maszyny (marka, model, rok)" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600" />
              <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Numer seryjny (jeśli jest)" className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm font-mono" />
              <button onClick={() => setForeign(false)} className="text-xs text-slate-500 underline">
                ← to jednak nasza maszyna
              </button>
            </div>
          )}

          {/* Customer — auto from serial, or picked by hand */}
          {customerId ? (
            <div className="text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-2 flex justify-between">
              <span>Klient: <strong className="text-slate-800">{customerName}</strong></span>
              <button onClick={() => { setCustomerId(''); setCustomerName(''); setClientQ('') }} className="text-xs text-slate-400 hover:text-slate-700">zmień</button>
            </div>
          ) : (
            <div>
              <input value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Klient: nazwa, NIP, miasto…" className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
              {clients.length > 0 && (
                <div className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100">
                  {clients.map((c) => (
                    <button key={c.id} onClick={() => { setCustomerId(c.id); setCustomerName(c.name) }} className="w-full text-left px-3 py-1.5 hover:bg-brand-50 text-sm">
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <label className="flex-1 text-xs text-slate-500">
              Rodzaj
              <select value={type} onChange={(e) => setType(e.target.value as typeof type)} className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
                <option value="gwarancyjny">Gwarancyjny</option>
                <option value="pogwarancyjny">Pogwarancyjny</option>
                <option value="przeglad">Przegląd</option>
              </select>
            </label>
          </div>

          <textarea value={fault} onChange={(e) => setFault(e.target.value)} rows={2} placeholder="Opis usterki (od klienta)" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600" />

          {error && <div className="text-sm text-red-700">{error}</div>}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="text-sm text-slate-500 px-3 py-2">Anuluj</button>
            <button onClick={() => void submit()} disabled={busy || !canSubmit} className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium">
              {busy ? 'Tworzę…' : 'Utwórz zgłoszenie'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
